import React, { useState } from "react";
import { Rating, Typography, Box, Button, Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Avatar, Paper, Tooltip } from "@mui/material";
import { Star, Flag, Group, BarChart, RateReview, Security, LocationOn, Groups, BusinessCenter, ReportGmailerrorred } from "@mui/icons-material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { format, parseISO } from "date-fns";

const Ratings = ({
  score = 0,
  title = "Customer Satisfaction Score",
  reviews = [],
  card = "",
  expandAll = null,
  placeDetails = {}
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const ratingsTitle = title
  const zip_code = placeDetails.zip

  const getRiskLevel = (score) => {
    if (score >= 9) return 'VERY HIGH RISK';
    if (score >= 7) return 'HIGH RISK';
    if (score >= 5) return 'MEDIUM RISK';
    if (score >= 3) return 'LOW RISK';
    return 'VERY LOW RISK';
  };

  const zipGradeScore = (grade, score) => {
    let riskText = '';
    if (score > 6) riskText = 'High Risk';
    else if (score > 2) riskText = 'Medium Risk';
    else riskText = 'Very Safe';
    return `Zip Code Grade: ${grade ? grade : 'N/A'} (${riskText})`;
  };

  const getRiskColor = (score) => {
    if (score >= 9) return 'error.main';           // Red
    if (score >= 7) return 'error.light';          // Orange-Red
    if (score >= 5) return 'warning.main';         // Amber
    if (score >= 3) return 'success.main';         // Green
    return 'primary.main';                         // Blue or safe color
  };


  const hoverHeadT2 = (
    <Box> <Typography fontWeight="bold">1 Mile</Typography> </Box>
  );

  const hoverHeadT3 = (
    <Box> <Typography fontWeight="bold">Crime grade reflects overall ZIP code based on national benchmarks.</Typography> </Box>
  );

  const hoverHeadT4 = (
    <Box> <Typography fontWeight="bold">Based on U.S. Census 5-Year ACS data for this ZIP code</Typography> </Box>
  )

  const hoverHeadT5 = (
    <Box> <Typography fontWeight="bold">0.1 Mile</Typography> </Box>
  );


  const hoveringTitle =
    card === "2" ? hoverHeadT2 : card === "3" ? hoverHeadT3 : card === "4" ? hoverHeadT4 : card === "5" ? hoverHeadT5 : "";

  const getCardIcon = (cardNumber) => [<RateReview color="primary" />, <Security color="error" />, <LocationOn color="secondary" />, <Groups color="info" />, <BusinessCenter color="warning" />, <ReportGmailerrorred color="error" />][cardNumber - 1] || null;


  const tool1 = (
    <Box>
      <Typography fontWeight="bold">Scoring (1–10)</Typography>
      <Typography variant="body2" mt={1}>
        Flagged reviews as % of total 1–2 star reviews:
      </Typography>
      <Typography variant="body2">≥30% → 7 points</Typography>
      <Typography variant="body2">10–29% → 5 points</Typography>
      <Typography variant="body2">&lt;10% → 3 points</Typography>

      <Typography variant="body2" mt={1}>
        Add rating-based score:
      </Typography>
      <Typography variant="body2">≤3.2 → +3 points</Typography>
      <Typography variant="body2">3.3–4.0 → +2 points</Typography>
      <Typography variant="body2">4.0 → +0 points</Typography>

      <Typography variant="body2" mt={1}>
        <strong>Cap total score at 10 points</strong>
      </Typography>
    </Box>
  );

  const tool2 = (
    <Box>
      <Typography fontWeight="bold">Scoring (1–10)</Typography>
      <Typography variant="body2" mt={1}>
        ≥75 incidents → 10 points
      </Typography>
      <Typography variant="body2">50–74 → 8 points</Typography>
      <Typography variant="body2">30–49 → 6 points</Typography>
      <Typography variant="body2">15–29 → 4 points</Typography>
      <Typography variant="body2">5–14 → 2 points</Typography>
      <Typography variant="body2">&lt;5 → 1 point</Typography>
    </Box>
  );

  const tool3 = (
    <Box>
      <Typography variant="body2">Grade 1-3 → (F)</Typography>
      <Typography variant="body2">Grade 4-6 → (C-D)</Typography>
      <Typography variant="body2">Grade 7-10 → (A-B)</Typography>
    </Box>
  );

  const tool4 = (
    <Box>
      <Typography fontWeight="bold">Scoring (1–10)</Typography>

      <Typography variant="body2" mt={1}>
        If <strong>2 or more</strong> of these are true →{" "}
        <strong>10 points</strong>:
      </Typography>
      <Typography variant="body2">• Median income &lt; $45,000</Typography>
      <Typography variant="body2">• Poverty rate ≥ 20%</Typography>
      <Typography variant="body2">• Unemployment rate ≥ 10%</Typography>

      <Typography variant="body2" mt={1}>
        If <strong>1</strong> condition is true → <strong>6 points</strong>
      </Typography>
      <Typography variant="body2">
        If <strong>none</strong> are true → <strong>2 points</strong>
      </Typography>
    </Box>
  );

  const tool5 = (
    <Box>
      <Typography fontWeight="bold">Scoring (1–10)</Typography>

      <Typography variant="body2" mt={1}>
        3 or more risky businesses → <strong>10 points</strong>
      </Typography>
      <Typography variant="body2">
        2 → <strong>7 points</strong>
      </Typography>
      <Typography variant="body2">
        1 → <strong>4 points</strong>
      </Typography>
      <Typography variant="body2">
        0 → <strong>1 point</strong>
      </Typography>
    </Box>
  );

  const tool6 = (
    <Box>
      <Typography fontWeight="bold">Scoring (1–10)</Typography>
      <Typography variant="body2" mt={1}>
        ≥75 Sex Offense → 10 points
      </Typography>
      <Typography variant="body2">50–74 → 8 points</Typography>
      <Typography variant="body2">30–49 → 6 points</Typography>
      <Typography variant="body2">15–29 → 4 points</Typography>
      <Typography variant="body2">5–14 → 2 points</Typography>
      <Typography variant="body2">&lt;5 → 1 point</Typography>
    </Box>
  );

  const hoveringRatingTitle = card === "1" ? tool1 : card === "2" ? tool2 : card === "3" ? tool3 : card === "4" ? tool4 : card === "5" ? tool5 : card === "6" ? tool6 : "";

  // Sorting state
  // const [sortField, setSortField] = useState("incident_date");
  // const [sortDirection, setSortDirection] = useState("desc");
  // Sorting state for reviews
  const [sortFieldReviews, setSortFieldReviews] = useState("datetime");
  const [sortDirectionReviews, setSortDirectionReviews] = useState("desc");
  // Sorting state for sex offenders
  const [sortRiskyBusiness, setSortRiskyBusiness] = useState("business_name");
  const [sortFieldSex, setSortFieldSex] = useState("sex_offender_name");
  const [sortDirectionSex, setSortDirectionSex] = useState("asc");
  // Sorting state for business risks
  const [sortDirectionBusiness, setSortDirectionBusiness] = useState("asc");
  const googleReviews = reviews.reviews;
  const flaggedReviews = reviews.flagged;
  const [reviewType, setReviewType] = useState('all');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const crimeRateReviews = reviews;

  const displayedReviews = reviewType === 'flagged' ? flaggedReviews : googleReviews;

  const handleToggle = () => {
    setSelectedBusiness(null)
    setShowDetails((prev) => !prev);
  }
  const hasReviews = Array.isArray(displayedReviews) && displayedReviews.length > 0;
  const hasSexOffender =
    Array.isArray(reviews.sex_offenders) && reviews.sex_offenders.length > 0;
  // const hasIncidents =
  //   Array.isArray(reviews.incidents) && reviews.incidents.length > 0;
  const hasBusinessRisks =
    Array.isArray(reviews.risky_places) && reviews.risky_places.length > 0;
  //const hasZipCrimes = true;
  const hasSocio = true;

  // Sync showDetails with expandAll prop (one-time effect)
  React.useEffect(() => {
    if (expandAll === true) setShowDetails(true);
    else if (expandAll === false) setShowDetails(false);
    // if null, do nothing
  }, [expandAll]);

  // Sort incidents by selected field and direction
  // const sortedIncidents = Array.isArray(reviews.incidents)
  //   ? [...reviews.incidents].sort((a, b) => {
  //     const aValue = a[sortField] || "";
  //     const bValue = b[sortField] || "";
  //     if (sortField === "incident_date") {
  //       if (!aValue) return 1;
  //       if (!bValue) return -1;
  //       const aDate = new Date(aValue);
  //       const bDate = new Date(bValue);
  //       return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
  //     } else {
  //       if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
  //       if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
  //       return 0;
  //     }
  //   })
  //   : [];

  // Use displayedReviews based on reviewType

  // Sort displayed reviews by selected field and direction
  const sortedReviews = Array.isArray(displayedReviews)
    ? [...displayedReviews].sort((a, b) => {
      const aValue = a[sortFieldReviews] || "";
      const bValue = b[sortFieldReviews] || "";
      if (sortFieldReviews === "rating") {
        return sortDirectionReviews === "asc"
          ? aValue - bValue
          : bValue - aValue;
      } else if (sortFieldReviews === "datetime") {
        if (!aValue) return 1;
        if (!bValue) return -1;
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortDirectionReviews === "asc" ? aDate - bDate : bDate - aDate;
      } else {
        if (aValue < bValue) return sortDirectionReviews === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirectionReviews === "asc" ? 1 : -1;
        return 0;
      }
    })
    : [];

  // Sort sex offenders by selected field and direction
  const sortedSexOffenders = Array.isArray(reviews.sex_offenders)
    ? [...reviews.sex_offenders].sort((a, b) => {
      const aValue = a[sortFieldSex] || "";
      const bValue = b[sortFieldSex] || "";
      if (sortFieldSex === "sex_offender_age") {
        return sortDirectionSex === "asc" ? aValue - bValue : bValue - aValue;
      } else {
        if (aValue < bValue) return sortDirectionSex === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirectionSex === "asc" ? 1 : -1;
        return 0;
      }
    })
    : [];

  // Sort risky_places by selected field and direction
  const sortedBusinessRisks = Array.isArray(reviews.risky_places)
    ? [...reviews.risky_places].sort((a, b) => {
      const aValue = a[sortRiskyBusiness] || "";
      const bValue = b[sortRiskyBusiness] || "";
      if (aValue < bValue) return sortDirectionBusiness === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirectionBusiness === "asc" ? 1 : -1;
      return 0;
    })
    : [];


  const exportReviewsToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Reviews");

    // Add header row
    const header = ["Author", "Rating", "Review", "Time"];
    worksheet.addRow(header);

    // Add data rows

    sortedReviews.forEach((reviews) => {
      worksheet.addRow([
        reviews.reviewer || "Unknown",
        reviews.rating,
        reviews.text,
        reviews.datetime
          ? format(parseISO(reviews.datetime), "MM/dd/yyyy hh:mm:ss a")
          : "",
      ]);
    });

    // Style headers
    worksheet.getRow(1).font = { bold: true };
    worksheet.columns.forEach((column) => {
      column.width = 30;
      column.alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };
    });

    // Create file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "reviews_data.xlsx");
  };

  // const exportIncidentsToExcel = async () => {
  //   const workbook = new ExcelJS.Workbook();
  //   const worksheet = workbook.addWorksheet("Incidents");

  //   // Add header row
  //   const header = [
  //     "City",
  //     "Date",
  //     "Incident Offense",
  //     "Description",
  //     "Crime Against",
  //   ];
  //   worksheet.addRow(header);

  //   // Add data rows
  //   sortedIncidents.forEach((incident) => {
  //     worksheet.addRow([
  //       incident.city_key,
  //       incident.incident_date
  //         ? format(parseISO(incident.incident_date), "MM/dd/yyyy hh:mm:ss a")
  //         : "",
  //       incident.incident_offense,
  //       incident.incident_offense_detail_description,
  //       incident.incident_offense_crime_against,
  //     ]);
  //   });

  //   // Style headers
  //   worksheet.getRow(1).font = { bold: true };
  //   worksheet.columns.forEach((column) => {
  //     column.width = 30;
  //     column.alignment = {
  //       vertical: "middle",
  //       horizontal: "left",
  //       wrapText: true,
  //     };
  //   });

  //   // Create file
  //   const buffer = await workbook.xlsx.writeBuffer();
  //   const blob = new Blob([buffer], {
  //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   });
  //   saveAs(blob, "incident_data.xlsx");
  // };

  const exportBusinessRisksToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Risky Places");

    const header = [
      "Risky Place"
    ];
    worksheet.addRow(header);

    // Add data rows
    sortedBusinessRisks.forEach((risky_place) => {
      worksheet.addRow([
        risky_place.business_name
      ]);
    });

    // Style headers
    worksheet.getRow(1).font = { bold: true };
    worksheet.columns.forEach((column) => {
      column.width = 30;
      column.alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };
    });

    // Generate buffer
    const buffer = await workbook.xlsx.writeBuffer();

    // Trigger download
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "RiskyPlaces.xlsx");
  };

  const exportSexOffendersToExcel = async (sexOffenders) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sex Offenders");

    // Define headers
    worksheet.columns = [
      { header: "Name", key: "name", width: 30 },
      { header: "State", key: "state", width: 15 },
      { header: "Location", key: "location", width: 30 },
      { header: "Age", key: "age", width: 10 },
      { header: "Gender", key: "gender", width: 10 },
    ];

    // Add rows
    sexOffenders.forEach((offender) => {
      worksheet.addRow({
        name: offender.sex_offender_name || "Unknown",
        state: offender.sex_offender_state,
        location: offender.sex_offender_location,
        age: offender.sex_offender_age,
        gender: offender.sex_offender_sex,
      });
    });

    // Align headers and cells
    worksheet.columns.forEach((col) => {
      col.alignment = { vertical: "middle", horizontal: "left" };
    });

    // Bold header row
    worksheet.getRow(1).font = { bold: true };

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "Sex_Offenders_Report.xlsx");
  };

  // Sorting handler
  // const handleSort = (field) => {
  //   if (sortField === field) {
  //     setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  //   } else {
  //     setSortField(field);
  //     setSortDirection("asc");
  //   }
  // };

  // Sorting handler for reviews
  const handleSortReviews = (field) => {
    if (sortFieldReviews === field) {
      setSortDirectionReviews((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortFieldReviews(field);
      setSortDirectionReviews("asc");
    }
  };

  // Sorting handler for sex offenders
  const handleSortSex = (field) => {
    if (sortFieldSex === field) {
      setSortDirectionSex((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortFieldSex(field);
      setSortDirectionSex("asc");
    }
  };

  const handleSortBusiness = (field) => {
    if (sortRiskyBusiness === field) {
      setSortDirectionBusiness((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortRiskyBusiness(field);
      setSortDirectionBusiness("asc");
    }
  };

  // Helper function to ucwords a string
  function ucwords(str) {
    return str.replace(/\b\w/g, (c) => c.toUpperCase());
  }


  const CrimeRateDetails = ({ data }) => {

    const preprocessData = (markdown) => {
      // Keep only content before "## Property Safety Score"
      return markdown.split("## Property Safety Score")[0].trim();
    };

    const parseMarkdownTable = (markdown) => {
      const lines = markdown.trim().split("\n").filter((line) => line.trim());
      if (lines.length < 2) return null;

      const headers = lines[0]
        .split("|")
        .map((h) => h.trim())
        .filter((h) => h);
      const rows = lines.slice(2).map((line) =>
        line
          .split("|")
          .map((cell) => cell.trim())
          .filter((cell) => cell)
      );

      return { headers, rows };
    };

    return (
      <div className="space-y-6">
        {(() => {
          const cleaned = preprocessData(data);
          const parsed = parseMarkdownTable(cleaned);
          if (!parsed) return null;

          return (
            <div className="p-4 rounded-xl shadow bg-white">

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  maxHeight: 400,
                  overflow: "auto",
                  width: { xs: "100%", md: "50%" },
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                      {parsed.headers.map((header, index) => (
                        <TableCell
                          key={index}
                          sx={{ fontWeight: 700, cursor: "pointer" }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsed.rows.map((row, index) => (
                      <TableRow key={index}>
                        {row.map((cell, j) => (
                          <TableCell key={j}>
                            {cell}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <Box
      bgcolor="#fff"
      borderRadius={2}
      boxShadow={2}
      mb={1}
      width="100%"
      sx={{
        flexDirection: { xs: 'column', sm: 'row' },
        px: { xs: 1, sm: 2 },
        py: { xs: 2, sm: 2 },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={{ xs: 2, sm: 1 }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1, sm: 0 }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          width="100%"
          justifyContent={{ xs: 'flex-start', sm: 'flex-start' }}
          mb={{ xs: card !== '3' ? 1 : 0, sm: 0 }}
        >
          {getCardIcon(card)}
          {hoveringTitle ? (
            <Tooltip title={hoveringTitle} arrow placement="top">
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  wordBreak: 'break-word',
                  maxWidth: { xs: '80vw', sm: 'none' }
                }}
              >
                {ratingsTitle}
              </Typography>
            </Tooltip>
          ) : (
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                wordBreak: 'break-word',
                maxWidth: { xs: '80vw', sm: 'none' }
              }}
            >
              {ratingsTitle}
            </Typography>
          )}
        </Box>
        {card !== '3' && (
          <Button
            variant="contained"
            size="small"
            onClick={handleToggle}
            sx={{
              flexShrink: 0,
              minWidth: 120,
              alignSelf: { xs: 'flex-start', sm: 'center' }, // Display left in mobile, center otherwise
            }}
          >
            {showDetails ? "Hide Details" : "View Details"}
          </Button>
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        {(card === "2" || card === "6" || card === "3") ? null : (
          <Rating value={Number(score) / 2} precision={0.5} readOnly />
        )}
        {hoveringRatingTitle &&
          <Tooltip title={hoveringRatingTitle} arrow placement="top">
            {(card === "2" || card === "6") ?
              <Typography color={getRiskColor(score)} fontWeight={600}>{getRiskLevel(score)}</Typography>
              : (
                <Typography variant={card === '3' ? 'subtitle1' : 'h6'} color={card === '3' ? 'secondary' : 'primary'} fontWeight={600}>
                  {card === "3" ? zipGradeScore(reviews.grade, score) : `${score} / 10`}
                </Typography>
              )
            }
          </Tooltip>
        }
      </Box>
      {showDetails ? (
        <>
          {card === "1" && (
            <>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      bgcolor: reviewType === 'flagged' ? '#ffeaea' : '#fef2f2',
                      boxShadow: 1,
                      cursor: 'pointer',
                      border: reviewType === 'flagged' ? '2px solid #d32f2f' : 'none',
                      '&:hover': { boxShadow: 4, backgroundColor: '#fde4e4' }
                    }}
                    onClick={() => setReviewType('flagged')}
                  >
                    <CardContent sx={{ p: { xs: 0.7, md: 2 } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Flag color="error" />
                        <Typography variant="body2" color="text.secondary">Flagged Reviews</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={600} color="error.main" textAlign="center">
                        {reviews.flagged ? reviews.flagged.length : 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      bgcolor: reviewType === 'all' ? '#ffeaea' : '#f5f3ff',
                      boxShadow: 1,
                      cursor: 'pointer',
                      border: reviewType === 'all' ? '2px solid #d32f2f' : 'none',
                      '&:hover': { boxShadow: 4, backgroundColor: '#fde4e4' }
                    }}
                    onClick={() => setReviewType('all')}
                  >
                    <CardContent sx={{ p: { xs: 0.7, md: 2 } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Group color="secondary" />
                        <Typography variant="body2" color="text.secondary">Total User Reviews</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={600} color="secondary.main" textAlign="center">
                        {reviews.user_ratings_total}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#fffbeb", boxShadow: 1 }}>
                    <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Star sx={{ color: "#fbbf24" }} />
                        <Typography variant="body2" color="text.secondary">Avg Rating</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={600} sx={{ color: "#d97706" }} textAlign="center">
                        {reviews.rating} / 5
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {hasReviews ? (
                <>
                  <Box display="flex" justifyContent="flex-end" sx={{ mb: 1, mt: { xs: 3, md: 0 } }}>
                    <Button variant="outlined" onClick={exportReviewsToExcel}>Export to Excel</Button>
                  </Box>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                          {[
                            { label: "Author", field: "reviewer" },
                            { label: "Rating", field: "rating" },
                            { label: "Review", field: "text" },
                            { label: "Time", field: "datetime" },
                          ].map((col, index) => (
                            <TableCell
                              key={index}
                              sx={{ fontWeight: 700, cursor: "pointer" }}
                              onClick={() => handleSortReviews(col.field)}
                            >
                              {col.label}
                              {sortFieldReviews === col.field && (
                                <span style={{ marginLeft: 4 }}>
                                  {sortDirectionReviews === "asc" ? "▲" : "▼"}
                                </span>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedReviews.map((review, index) => (
                          <TableRow key={index} sx={(review.rating === '1' || review.rating === '2') ? { bgcolor: '#ffebee', color: '#b71c1c', fontWeight: 700 } : {}}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {review.reviewer_url ? (
                                  <a
                                    href={review.reviewer_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                                  >
                                    <Avatar src={review.reviewer_avatar} alt={review.reviewer} sx={{ width: 32, height: 32 }} />
                                  </a>
                                ) : (
                                  <Avatar src={review.reviewer_avatar} alt={review.reviewer} sx={{ width: 32, height: 32 }} />
                                )}
                                {review.url ? (
                                  <a
                                    href={review.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "none", color: "#0366d6", width: "150px" }}
                                  >
                                    {review.reviewer || "Unknown"}
                                  </a>
                                ) : (
                                  <span style={{ width: "150px" }}>{review.reviewer || "Unknown"}</span>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Rating value={Number(review.rating)} readOnly size="small" />
                            </TableCell>
                            <TableCell>{review.text}</TableCell>
                            <TableCell style={{ width: "200px" }}>
                              {review.datetime
                                ? format(parseISO(review.datetime), "MM/dd/yyyy hh:mm:ss a")
                                : ""}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                (reviewType === "all") &&
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ mt: 2, mb: 1, p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffe0b2' }}>
                  <Typography variant="h6" color="text.secondary" align="center">
                    Insufficient review data. Not included in final score.
                  </Typography>
                </Box>
              )}
            </>
          )}
          {/* {card === "2" && (
            <>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      bgcolor: "#fef2f2",
                      boxShadow: 2,
                      borderRadius: 3,
                      transition: "0.3s",
                      "&:hover": { boxShadow: 4 },
                    }}
                  >
                    <CardContent sx={{ p: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Group color="success" />
                        <Typography variant="body2" color="text.secondary">Total Incidents</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={600} color="success.main" textAlign="center">
                        {reviews.incidents_count}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {hasIncidents && (
                <>
                  <Box display="flex" justifyContent="flex-end" sx={{ mb: 1, mt: { xs: 2, md: 0 } }}>
                    <Button variant="outlined" onClick={exportIncidentsToExcel}>Export to Excel</Button>
                  </Box>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {[
                            { label: "City", field: "city_key" },
                            { label: "Date", field: "incident_date" },
                            { label: "Incident Offense", field: "incident_offense" },
                            { label: "Description", field: "incident_offense_detail_description" },
                            { label: "Crime Against", field: "incident_offense_crime_against" },
                          ].map((col, index) => (
                            <TableCell
                              key={index}
                              sx={{ backgroundColor: "#f1f5f9", fontWeight: 700, cursor: "pointer" }}
                              onClick={() => handleSort(col.field)}
                            >
                              {col.label}
                              {sortField === col.field && (
                                <span style={{ marginLeft: 4 }}>
                                  {sortDirection === "asc" ? "▲" : "▼"}
                                </span>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedIncidents.map((review, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{review.city_key}</TableCell>
                            <TableCell style={{ width: "200px" }}>
                              {review.incident_date
                                ? format(parseISO(review.incident_date), "MM/dd/yyyy hh:mm:ss a")
                                : ""}
                            </TableCell>
                            <TableCell>{review.incident_offense}</TableCell>
                            <TableCell>{review.incident_offense_detail_description}</TableCell>
                            <TableCell>{review.incident_offense_crime_against}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )} */}
          {card === "2" && (
            <>
              <CrimeRateDetails data={crimeRateReviews} />
            </>
          )}
          {card === "4" && hasSocio && (
            <>
              <Typography variant="subtitle1" fontWeight={600}>ZIP Code: {zip_code}</Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  maxHeight: 400,
                  overflow: "auto",
                  width: { xs: "100%", md: "50%" },
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Median Income ($)</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Poverty Rate</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Unemployed</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Labor Force</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow key={1}>
                      <TableCell>{reviews.median_income}</TableCell>
                      <TableCell>{reviews.poverty_count}</TableCell>
                      <TableCell>{reviews.unemployed}</TableCell>
                      <TableCell>{reviews.labor_force}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {card === "5" && (
            <>
              <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      bgcolor: "#ecfdf5",
                      boxShadow: 2,
                      borderRadius: 3,
                      transition: "0.3s",
                      "&:hover": { boxShadow: 4 },
                    }}
                  >
                    <CardContent sx={{ p: 1 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <BarChart color="success" />
                        <Typography variant="body2" color="text.secondary">Total Risky Places</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={600} color="success.main" textAlign="center">
                        {sortedBusinessRisks.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {hasBusinessRisks ? (
                <>
                  <Box display="flex" justifyContent="flex-end" alignItems="flex-start" sx={{ mt: { xs: 2, md: 0 } }}>
                    <Button variant="outlined" onClick={exportBusinessRisksToExcel} sx={{ ml: 'auto' }}>
                      Export to Excel
                    </Button>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    gap={1}
                  >
                    <Box flex={1}>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                          mt: 2,
                          borderRadius: 2,
                          maxHeight: 400,
                          overflow: "auto",
                          width: "100%",
                        }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                              {[{ label: "Risky Place", field: "business_name" }].map((col, index) => (
                                <TableCell
                                  key={index}
                                  sx={{ fontWeight: 700, cursor: "pointer" }}
                                  onClick={() => handleSortBusiness(col.field)}
                                >
                                  {col.label}
                                  {sortRiskyBusiness === col.field && (
                                    <span style={{ marginLeft: 4 }}>
                                      {sortDirectionBusiness === "asc" ? "▲" : "▼"}
                                    </span>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedBusinessRisks.map((bdata, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <span
                                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => setSelectedBusiness(bdata)}
                                  >
                                    {ucwords(bdata.business_name)}
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                    <Box
                      flex={1}
                      minWidth={320}
                      display={{ xs: 'block', md: 'block' }}
                      sx={{
                        mt: { xs: 0, md: 0 },
                        // Hide on mobile if no selection, show on desktop always
                        ...(typeof window !== "undefined" && window.innerWidth < 900 && (!showDetails || !selectedBusiness || !selectedBusiness.latitude || !selectedBusiness.longitude)
                          ? { display: 'none' }
                          : {}),
                      }}
                    >
                      {showDetails && selectedBusiness && selectedBusiness.latitude && selectedBusiness.longitude && (
                        <Box sx={{ mt: { xs: 0, md: 1 }, ml: { xs: 0, md: 1 }, pr: { xs: 2, md: 0 } }}>
                          <Typography variant="subtitle1" fontWeight={600} mb={1}>
                            Place: {ucwords(selectedBusiness.business_name)}
                          </Typography>
                          <iframe
                            title="Google Map"
                            width="100%"
                            height="300"
                            style={{ border: 0, borderRadius: 8 }}
                            src={`https://www.google.com/maps?q=${selectedBusiness.latitude},${selectedBusiness.longitude}&z=16&output=embed`}
                            allowFullScreen
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ mt: 2, mb: 1, p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffe0b2' }}>
                  <Typography variant="h6" color="text.secondary" align="center">
                    Nearby Business Risks not found
                  </Typography>
                </Box>
              )}
            </>
          )}
          {card === "6" && (
            <>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: "#fef2f2", boxShadow: 1 }}>
                    <CardContent sx={{ p: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Group color="success" />
                        <Typography variant="body2" color="text.secondary">Total Sex Offenders</Typography>
                      </Box>
                      <Typography variant="h6" fontWeight={600} color="success.main" textAlign="center">
                        {reviews.sex_offenders_count}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {hasSexOffender ? (
                <>
                  <Box display="flex" justifyContent="flex-end" sx={{ mb: 1, mt: { xs: 2, md: 0 } }}>
                    <Button variant="outlined" onClick={() => exportSexOffendersToExcel(reviews.sex_offenders)} >
                      Export to Excel
                    </Button>
                  </Box>
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      maxHeight: 400,
                      overflow: "auto",
                      width: { xs: "100%", md: "50%" },
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f1f5f9" }}>
                          {[
                            { label: "Name", field: "sex_offender_name" },
                            { label: "State", field: "sex_offender_state" },
                            { label: "Location", field: "sex_offender_location" },
                            { label: "Age", field: "sex_offender_age" },
                            { label: "Gender", field: "sex_offender_sex" },
                          ].map((col, index) => (
                            <TableCell
                              key={index}
                              sx={{ fontWeight: 700, cursor: "pointer" }}
                              onClick={() => handleSortSex(col.field)}
                            >
                              {col.label}
                              {sortFieldSex === col.field && (
                                <span style={{ marginLeft: 4 }}>
                                  {sortDirectionSex === "asc" ? "▲" : "▼"}
                                </span>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedSexOffenders.map((review, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {review.sex_offender_url ? (
                                  <a
                                    href={review.sex_offender_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                  >
                                    <Avatar src={review.sex_offender_image_url} alt={review.sex_offender_name} sx={{ width: 32, height: 32 }} />
                                  </a>
                                ) : (
                                  <Avatar src={review.sex_offender_image_url} alt={review.sex_offender_name} sx={{ width: 32, height: 32 }} />
                                )}
                                {review.sex_offender_url ? (
                                  <a
                                    href={review.sex_offender_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "none", color: "#0366d6", width: "150px" }}
                                  >
                                    {review.sex_offender_name || "Unknown"}
                                  </a>
                                ) : (
                                  <span style={{ width: "150px" }}>{review.sex_offender_name || "Unknown"}</span>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{review.sex_offender_state}</TableCell>
                            <TableCell>{review.sex_offender_location}</TableCell>
                            <TableCell>{review.sex_offender_age}</TableCell>
                            <TableCell>{review.sex_offender_sex}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ mt: 2, mb: 1, p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffe0b2' }}>
                  <Typography variant="h6" color="text.secondary" align="center">
                    Sexual Offense not found
                  </Typography>
                </Box>
              )}
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" mt={1}></Typography>
      )}
    </Box>
  );
};

export default Ratings;
